import { MODULE_ENTRIES, getModuleEntryBySlug, type ModuleEntry } from '@/config/moduleEntries';
import { CHOOSE_APP_PATH, MODULE_HUB_PATH, REGISTER_PATH } from '@/constants/authRoutes';

export type AuthPanelMode = 'login' | 'register' | 'hub' | 'master' | 'forgot' | 'reset';

export interface AuthPanelContent {
  eyebrow: string;
  heading: string;
  description: string;
  highlights: string[];
}

const MODULE_COPY: Partial<Record<string, { login: AuthPanelContent; register: AuthPanelContent }>> = {
  POS: {
    login: {
      eyebrow: 'Point of Sale',
      heading: 'Open your store and ring up sales',
      description:
        'Sign in to run the POS terminal, manage catalog and stock, and reconcile daily sales — all in one retail workspace.',
      highlights: [
        'Fast checkout with barcode and held bills',
        'Real-time inventory and customer wallet',
        'Shift, returns, and store-level reports',
      ],
    },
    register: {
      eyebrow: 'Point of Sale',
      heading: 'Start selling with Fattah POS',
      description: 'Create your organization, then choose POS and any other products you need.',
      highlights: [
        'Guided setup: store, products, customers',
        'Multi-store and tax-ready catalog',
        'One login across Fattah when you grow',
      ],
    },
  },
  CRM: {
    login: {
      eyebrow: 'CRM',
      heading: 'Pick up your pipeline where you left off',
      description:
        'Sign in to manage leads, contacts, deals, and activities — focused CRM navigation without unrelated modules.',
      highlights: [
        'Leads, companies, and deal pipelines',
        'Campaigns, tasks, and follow-ups',
        'Sales reports for your team',
      ],
    },
    register: {
      eyebrow: 'CRM',
      heading: 'Grow revenue with a modern CRM',
      description:
        'Register your team to capture leads, track deals, and coordinate sales. Already on Fattah? Add CRM to your organization in one step.',
      highlights: [
        'Pipeline and deal stages out of the box',
        'Connect marketing and customer data',
        'Same organization, one subscription model',
      ],
    },
  },
  HR: {
    login: {
      eyebrow: 'HRMS',
      heading: 'Manage your workforce in one place',
      description:
        'Sign in for employees, attendance, leave, and payroll — HR workflows scoped to your organization.',
      highlights: [
        'Employee profiles and org structure',
        'Attendance, leave, and approvals',
        'Payroll runs and HR analytics',
      ],
    },
    register: {
      eyebrow: 'HRMS',
      heading: 'Launch HR for your growing team',
      description:
        'Set up HRMS for hiring, time tracking, and payroll. If you already use Fattah, verify your password to add HR to your profile.',
      highlights: [
        'Onboarding checklist for first employees',
        'Attendance policies and devices',
        'Unified login with your other Fattah apps',
      ],
    },
  },
  FINANCE: {
    login: {
      eyebrow: 'Finance',
      heading: 'Control books and cash flow',
      description:
        'Sign in to your general ledger, receivables, payables, and banking — finance-only workspace for your accountants.',
      highlights: [
        'Chart of accounts and journal entries',
        'Invoices, payments, and bank reconciliation',
        'Financial reports and period close',
      ],
    },
    register: {
      eyebrow: 'Finance',
      heading: 'Bring accounting onto one platform',
      description:
        'Start finance setup with fiscal periods and accounts. Existing Fattah customer? Add Finance without a second organization.',
      highlights: [
        'Double-entry ready foundation',
        'Receivables and payables in one hub',
        'Extends POS and procurement when enabled',
      ],
    },
  },
  PROJECTS: {
    login: {
      eyebrow: 'Projects',
      heading: 'Deliver projects on schedule',
      description:
        'Sign in to plan work, track tasks and sprints, and log time — project delivery without leaving your workspace.',
      highlights: [
        'Projects, tasks, boards, and sprints',
        'Timesheets and resource planning',
        'Portfolio and project analytics',
      ],
    },
    register: {
      eyebrow: 'Projects',
      heading: 'Run projects with your team',
      description:
        'Create your workspace for tasks, timelines, and billing-ready timesheets. Add Projects to an existing Fattah org anytime.',
      highlights: [
        'Kanban, Gantt, and sprint planning',
        'Billable time and approvals',
        'Works alongside CRM and Finance',
      ],
    },
  },
  DESK: {
    login: {
      eyebrow: 'Help Desk',
      heading: 'Resolve customer issues faster',
      description:
        'Sign in to tickets, shared inbox, and knowledge base — support tools built for your service team.',
      highlights: [
        'Tickets, inbox, and live chat',
        'KB articles and canned responses',
        'SLAs, automation, and support reports',
      ],
    },
    register: {
      eyebrow: 'Help Desk',
      heading: 'Stand up customer support',
      description:
        'Register to manage tickets and email in one place. Already on Fattah? Add Help Desk to the same organization.',
      highlights: [
        'Omnichannel inbox and workflows',
        'Self-service knowledge base',
        'Customer context from shared CRM data',
      ],
    },
  },
  PROCUREMENT: {
    login: {
      eyebrow: 'Procurement',
      heading: 'Run procure-to-pay with confidence',
      description:
        'Sign in for purchase requests, POs, goods receipt, and vendor bills — procurement scoped to your supply chain team.',
      highlights: [
        'RFQs, quotations, and purchase orders',
        'Goods receipt and vendor bills',
        'Approvals and supplier catalogs',
      ],
    },
    register: {
      eyebrow: 'Procurement',
      heading: 'Digitize purchasing and suppliers',
      description:
        'Start procurement for your organization. Use an existing Fattah login to add purchasing without duplicate tenants.',
      highlights: [
        'Supplier master and contracts',
        'Approval workflows',
        'Tied to inventory and finance',
      ],
    },
  },
  WAREHOUSE: {
    login: {
      eyebrow: 'Warehouse',
      heading: 'Move inventory with precision',
      description:
        'Sign in for locations, picks, transfers, and cycle counts — warehouse operations in a dedicated WMS workspace.',
      highlights: [
        'Pick waves and transfer orders',
        'Dock, cross-dock, and mobile WMS',
        'Labor and accuracy reporting',
      ],
    },
    register: {
      eyebrow: 'Warehouse',
      heading: 'Activate warehouse management',
      description:
        'Register WMS for your fulfillment team. Add to your existing Fattah organization when you already have an account.',
      highlights: [
        'Locations, zones, and movements',
        'Integrated with POS and procurement',
        'Cycle counts and inventory accuracy',
      ],
    },
  },
  SOCIAL: {
    login: {
      eyebrow: 'Social',
      heading: 'Engage audiences across channels',
      description:
        'Sign in to publish, monitor inbox, and measure campaigns — social marketing in one workspace.',
      highlights: [
        'Publishing calendar and approvals',
        'Inbox and listening',
        'Campaign and reputation insights',
      ],
    },
    register: {
      eyebrow: 'Social',
      heading: 'Start social marketing',
      description:
        'Create your social workspace for channels and content. Extend your Fattah org with Social when you are ready.',
      highlights: [
        'Multi-channel publishing',
        'Team approvals and workflows',
        'Analytics tied to your brand',
      ],
    },
  },
  LEAD_GEN: {
    login: {
      eyebrow: 'Lead Generation',
      heading: 'Turn traffic into qualified leads',
      description:
        'Sign in to forms, landing pages, scoring, and routing — acquisition tools for marketing ops.',
      highlights: [
        'Landing pages and A/B tests',
        'Lead scoring and routing rules',
        'Attribution and ROI reporting',
      ],
    },
    register: {
      eyebrow: 'Lead Generation',
      heading: 'Capture and qualify more leads',
      description:
        'Register to launch forms and campaigns. Add Lead Gen to your Fattah organization with your existing password.',
      highlights: [
        'Builder for forms and pages',
        'Visitor tracking and enrichment',
        'Hand-off to CRM when enabled',
      ],
    },
  },
  MARKETING: {
    login: {
      eyebrow: 'Marketing',
      heading: 'Orchestrate campaigns and journeys',
      description:
        'Sign in for email campaigns, segments, and automation — marketing execution without leaving your module.',
      highlights: [
        'Campaigns, templates, and journeys',
        'Audience segments and lifecycle',
        'Performance and ROI dashboards',
      ],
    },
    register: {
      eyebrow: 'Marketing',
      heading: 'Launch lifecycle marketing',
      description:
        'Set up marketing for your brand. Already use Fattah? Verify your email and password to add Marketing to your org.',
      highlights: [
        'Email and journey automation',
        'Customer segments from your data',
        'Connects to CRM and Lead Gen',
      ],
    },
  },
};

const HUB_PANEL: AuthPanelContent = {
  eyebrow: 'Fattah platform',
  heading: 'Choose the product you need today',
  description:
    'Each application has its own sign-in and workspace. Pick POS, CRM, HR, Finance, or another module — you only see what you subscribe to.',
  highlights: [
    'One organization, many applications',
    'Role-based access per module',
    'Sign in to the product you use daily',
  ],
};

const MASTER_LOGIN_PANEL: AuthPanelContent = {
  eyebrow: 'Platform operations',
  heading: 'Master console access',
  description:
    'Sign in to manage organizations, plans, and platform-wide settings. This area is for Fattah operators only.',
  highlights: [
    'Organizations and subscriptions',
    'Plans and platform analytics',
    'Not for tenant business users',
  ],
};

const FORGOT_PANEL: AuthPanelContent = {
  eyebrow: 'Account recovery',
  heading: 'Reset your password',
  description:
    'Enter the email on your account and we will send a secure link to choose a new password.',
  highlights: [
    'Works for all Fattah module sign-ins',
    'Link expires for your security',
    'Return to your module sign-in after reset',
  ],
};

const RESET_PANEL: AuthPanelContent = {
  eyebrow: 'Account recovery',
  heading: 'Choose a new password',
  description:
    'Set a strong password to protect your organization. You will sign in again with your module after this step.',
  highlights: [
    'Minimum complexity enforced',
    'Same email across all your apps',
    'Contact support if the link expired',
  ],
};

const DEFAULT_LOGIN: AuthPanelContent = {
  eyebrow: 'Sign in',
  heading: 'Welcome back',
  description: 'Sign in with the email and password for your organization. Your workspace opens after authentication.',
  highlights: ['Secure session per module', 'Role-based permissions', 'Optional two-factor authentication'],
};

const DEFAULT_REGISTER: AuthPanelContent = {
  eyebrow: 'Get started',
  heading: 'Create your organization',
  description: 'Register in minutes, then choose the Fattah products your team needs.',
  highlights: ['Pick products on the next screen', 'One organization, one login', 'Guided setup'],
};

function copyForModule(entry: ModuleEntry, mode: 'login' | 'register'): AuthPanelContent {
  const block = MODULE_COPY[entry.erpModule];
  if (block) {
    return mode === 'register' ? block.register : block.login;
  }
  const label = entry.label;
  return mode === 'register'
    ? {
        eyebrow: label,
        heading: `Get started with ${label}`,
        description: `Register your organization for ${label}, then complete product setup.`,
        highlights: [`Dedicated ${label} workspace`, 'Guided onboarding', 'Extend with other Fattah apps'],
      }
    : {
        eyebrow: label,
        heading: `Sign in to ${label}`,
        description: `Access your ${label} workspace with your work email. Only ${label} navigation and data appear after sign-in.`,
        highlights: [`${label} dashboard and workflows`, 'Team permissions', 'Secure cloud access'],
      };
}

function slugFromPath(pathname: string): string | null {
  const parts = pathname.split('/').filter(Boolean);
  if (parts.length === 0) return null;
  const first = parts[0].toLowerCase();
  if (['forgot-password', 'reset-password', 'master', 'oauth'].includes(first)) {
    return null;
  }
  const bySlug = getModuleEntryBySlug(first);
  if (bySlug) return bySlug.slug;
  return null;
}

export function resolveAuthPanelContent(pathname: string): AuthPanelContent {
  const path = pathname.split('?')[0].toLowerCase();

  if (path.includes('forgot-password')) return FORGOT_PANEL;
  if (path.includes('reset-password')) return RESET_PANEL;
  if (path.includes('/master/login')) return MASTER_LOGIN_PANEL;

  if (path === MODULE_HUB_PATH || path === '/login') {
    return DEFAULT_LOGIN;
  }
  if (path === REGISTER_PATH) {
    return DEFAULT_REGISTER;
  }
  if (path === CHOOSE_APP_PATH) {
    return HUB_PANEL;
  }

  const isRegister = path.includes('/register');
  const isLogin = path.includes('/login');
  const slug = slugFromPath(pathname);
  const entry = slug ? getModuleEntryBySlug(slug) : undefined;

  if (entry) {
    if (isRegister) return copyForModule(entry, 'register');
    if (isLogin) return copyForModule(entry, 'login');
    return copyForModule(entry, 'login');
  }

  if (isRegister) return DEFAULT_REGISTER;
  if (isLogin) return DEFAULT_LOGIN;

  return HUB_PANEL;
}

/** All module slugs that have dedicated panel copy (for tests/docs). */
export function authPanelModuleSlugs(): string[] {
  return MODULE_ENTRIES.map((e) => e.slug);
}
